import {
  Alert,
  Button,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { MyLeagueRegistrationResponse, LeagueUpcomingSessionResponse } from "@bw/shared/types/generated";
import { formatDateTime } from "@bw/shared/utils";
import { useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { useMyLeagueRegistrations } from "../hooks/useMyLeagueRegistrations";
import { useWithdrawLeagueRegistration } from "../hooks/useWithdrawLeagueRegistration";

function humanizeToken(token: string | null | undefined): string {
  if (!token) return "";
  const spaced = token.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function MyLeaguesPage() {
  const [params, setParams] = useSearchParams();
  const rawPage = Number(params.get("league_page"));
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const { data, isLoading, isError, refetch } = useMyLeagueRegistrations(page);
  const registrations = data?.items ?? [];
  function changePage(nextPage: number) {
    const next = new URLSearchParams(params);
    next.set("league_page", String(nextPage));
    setParams(next);
  }

  return (
    <Page title="My leagues" hideTitle>
      {isLoading ? (
        <Typography color="text.secondary" role="status">
          Loading your leagues…
        </Typography>
      ) : isError ? (
        <Alert
          severity="error"
          role="alert"
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        >
          We couldn&apos;t load your leagues. Please try again.
        </Alert>
      ) : data !== undefined && registrations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Typography color="text.secondary">
              {page > 1 ? "No leagues on this page." : "You're not in any leagues yet."}
            </Typography>
            <Button component={RouterLink} to="/compete/leagues">
              Find a league
            </Button>
          </Stack>
        </Paper>
      ) : (
        <List disablePadding sx={{ display: "grid", gap: 1.5 }}>
          {registrations.map((registration) => (
            <LeagueCard key={registration.registration_id} reg={registration} />
          ))}
        </List>
      )}
      {data && !isError && (page > 1 || data.has_more) ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>Previous leagues</Button>
          <Button disabled={!data.has_more} onClick={() => changePage(page + 1)}>Next leagues</Button>
        </Stack>
      ) : null}
    </Page>
  );
}

function LeagueCard({ reg }: { reg: MyLeagueRegistrationResponse }) {
  const isRanked = reg.standing_rank != null && reg.standing_rank > 0;
  const statusKey = reg.status.toLowerCase();
  const format = humanizeToken(reg.league_format);
  const context = [
    reg.season_name,
    reg.division_name,
    reg.facility_name,
    format,
  ].filter(Boolean);

  return (
    <ListItem
      disablePadding
      sx={{
        display: "block",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <ListItemButton
        {...(reg.detail_url ? { component: RouterLink, to: reg.detail_url } : { component: "div" as const, tabIndex: -1 })}
        disabled={!reg.detail_url}
        sx={{ alignItems: "flex-start" }}
      >
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Typography sx={{ fontWeight: 800 }}>{reg.league_name}</Typography>
            <Chip
              label={humanizeToken(reg.status)}
              data-status={reg.status}
              size="small"
              color={statusKey === "registered" ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            {context.map((fact, index) => (
              <Typography
                key={`${fact}-${index}`}
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  ...(index > 0 && {
                    "&::before": {
                      content: '"·"',
                      color: "text.disabled",
                    },
                  }),
                }}
              >
                {fact}
              </Typography>
            ))}
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {isRanked ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  #{reg.standing_rank}
                </Typography>
                <Typography variant="body2">
                  {reg.wins}-{reg.losses}
                </Typography>
                <Typography variant="body2">{reg.points} pts</Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not ranked yet
              </Typography>
            )}
          </Stack>
        </Stack>
      </ListItemButton>
      <Stack spacing={1.5} sx={{ px: 2, pb: 1.5 }}>
        <UpcomingSession title="Next registered session" value={reg.next_registered_session} />
        <UpcomingSession title="Next available session" value={reg.next_available_session} />
      </Stack>
      {statusKey === "registered" ? (
        <WithdrawControl
          registrationId={reg.registration_id}
          divisionName={reg.division_name}
        />
      ) : null}
    </ListItem>
  );
}

function UpcomingSession({ title, value }: { title: string; value?: LeagueUpcomingSessionResponse | null }) {
  if (!value) return null;
  const content = <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2">Session {value.session.session_number} · {formatDateTime(value.session.start_time)}</Typography>
      <Typography variant="caption" color="text.secondary">Times shown in your device’s time zone.</Typography>
    </Stack>;
  return <Paper variant="outlined" sx={{ overflow: "hidden" }}>
    {value.url ? <ListItemButton
      component={RouterLink}
      to={value.url}
      aria-label={`View ${title.toLowerCase()}`}
      sx={{ p: 1.5 }}
    >
      {content}
    </ListItemButton> : <Stack sx={{ p: 1.5 }}>{content}</Stack>}
  </Paper>;
}

function WithdrawControl({
  registrationId,
  divisionName,
}: {
  registrationId: string;
  divisionName: string;
}) {
  const withdraw = useWithdrawLeagueRegistration();
  const [confirming, setConfirming] = useState(false);
  const firedRef = useRef(false);

  function handleConfirm() {
    if (firedRef.current) return;
    firedRef.current = true;
    withdraw.mutate(registrationId, {
      onSuccess: () => {
        firedRef.current = false;
        setConfirming(false);
      },
      onError: () => {
        firedRef.current = false;
      },
    });
  }

  return (
    <Stack spacing={1} sx={{ px: 2, pb: 1.5, alignItems: "flex-start" }}>
      {!confirming ? (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setConfirming(true)}
        >
          Withdraw
        </Button>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2">Withdraw from {divisionName}?</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={handleConfirm}
              disabled={withdraw.isPending}
            >
              {withdraw.isPending ? "Withdrawing…" : "Confirm"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setConfirming(false)}
              disabled={withdraw.isPending}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
      {withdraw.isError ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t withdraw you. Please try again.
        </Alert>
      ) : null}
    </Stack>
  );
}
